import { AfterViewInit, Directive, DoCheck, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Stripe } from '@df/module-stripe/stripe.namespace';
import { StripeService } from '@df/module-stripe/stripe.service';
import { BehaviorSubject } from 'rxjs';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class AbstractStripeComponent<T extends Stripe.ICheckoutPaymentMethod, C extends Stripe.ICheckoutPaymentMethodConfig>
    extends AbstractCheckoutPayment<T>
    implements OnInit, OnDestroy, AfterViewInit, DoCheck
{
    /**
     * Component ready to display model
     */
    protected componentReady$ = new BehaviorSubject(false);

    get componentReady() {
        return this.componentReady$.getValue();
    }

    protected readyCheck() {
        const ready = this.isComponentReady();
        if (ready !== this.componentReady$.value) {
            this.componentReady$.next(ready);
        }
    }

    /**
     * Payment method code related to component
     * @type {string}
     */
    protected abstract override readonly methodCode: Stripe.PaymentMethod;

    protected stripe?: Stripe.Instance;

    protected elements?: Stripe.IElementsInstance;

    protected stripeService = inject(StripeService);

    override ngOnInit() {}

    override ngAfterViewInit(): void {
        if (this.canDisplayElements()) {
            super.ngOnInit();
            super.ngAfterViewInit();
        }
    }

    /**
     * Checks if elements can be displayed (template is correct for example)
     * @returns {boolean}
     */
    protected abstract canDisplayElements(): boolean;

    /**
     * Returns gran total from basket
     * @returns {number}
     */
    protected getTotal(): number | undefined {
        return this.app.customer.basket.data?.totals?.grand_total;
    }

    //////////////////////////////////////////
    /////
    /////   Payment method configuration
    /////
    //////////////////////////////////////////

    /**
     * If payment method should be initiated before selecting
     * @returns {boolean}
     */
    protected override preload(): boolean {
        return true;
    }

    //////////////////////////////////////////
    /////
    /////   Script methods
    /////
    //////////////////////////////////////////

    /**
     * Returns client api key
     * @returns {string}
     */
    protected getApiKey(): string | undefined {
        return this.method?.config?.initParams?.apiKey;
    }

    protected getConfig(): C | undefined {
        return this.method?.config as C;
    }

    /**
     * On script loasd success callback
     */
    protected override onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();

        const apiKey = this.getApiKey();
        try {
            this.stripe = this.stripeService.getStripe(apiKey, this.getStripeOptions());
            this.onStripeCreated();
            this.createElements();
        } catch (e) {
            this.onScriptLoadError();
        }
    }

    /**
     * Returns script urls
     * @returns {string[]}
     */
    protected getJsAssets(): string[] {
        return [Stripe.Scripts.JS];
    }

    protected abstract onStripeCreated(): void;

    /**
     * Creates elements object
     */
    protected createElements(): void {
        this.clear();
        try {
            this.elements = this.stripe?.elements(this.getElementsOptions());
            this.onElementsCreated();
        } catch (e) {
            console.error('Error creating stripe elements', e);
        }

        this.readyCheck();
        this.detectChanges();
    }

    //////////////////////////////////////////
    /////
    /////   Stripe methods
    /////
    //////////////////////////////////////////

    /**
     * Returns stripe options
     * @returns {Stripe.InstanceOptions}
     */
    protected getStripeOptions(): Stripe.InstanceOptions {
        return {};
    }

    /**
     * Returns elements options
     * @param {string} clientSecret
     * @returns {Stripe.IElementsIntanceOptions}
     */
    protected getElementsOptions(): Stripe.IElementsIntanceOptions {
        const options: Stripe.IElementsIntanceOptions = {};

        const config = this.getConfig();
        if (config && config.initParams?.locale) {
            options.locale = config.initParams.locale;
        }
        return options;
    }

    /**
     * Clears data before creating new elements
     */
    protected clear(): void {
        this.elements = undefined;
        this.componentReady$.next(false);
        this.detectChanges();
    }

    /**
     * On elements object created callback
     */
    protected abstract onElementsCreated(): void;

    /**
     * Returns if component is ready
     * @returns {boolean}
     */
    protected abstract isComponentReady(): boolean;

    //////////////////////////////////////////
    /////
    /////   Errors
    /////
    //////////////////////////////////////////

    /**
     * Returns standard payment error
     * @returns {string}
     */
    protected getError(): string {
        return Checkout.GENERAL_PAYMENT_ERROR_MESSAGE;
    }

    //////////////////////////////////////////
    /////
    /////   Checkout
    /////
    //////////////////////////////////////////

    /**
     * Checks if component is ready to use
     * @return {boolean}
     */
    protected override canUse(): boolean {
        return super.canUse() && this.componentReady$.getValue();
    }
}
